<template>
  <div class="row">
    <div class="col-12">
      <page-header :page="pageConfig">
        <div class="col-12 col-md-10 d-flex bg-light-gray rounded py-3">
          <v-select
            v-model="categorySelected"
            class="w-100 mr-3 custom-v-select"
            :options="categoriesOptions"
            label="name"
            :clearable="false"
          >
            <template #selected-option="{ name, icon }">
              <i class="mr-2" :class="iconClass(icon)" aria-hidden="true"></i>
              {{ name }}
            </template>
            <template #option="{ name, icon }">
              <div class="row">
                <div class="col-1">
                  <i :class="iconClass(icon)" aria-hidden="true"></i>
                </div>
                <div class="col-4">
                  <span>{{ name }}</span>
                </div>
              </div>
            </template>
          </v-select>
          <action-button
            id="modal-btn-cancelar"
            variant="success"
            type="button"
            i="fas fa-filter"
            @click="setFilterByCategory"
          >
            Filtrar
          </action-button>
        </div>
      </page-header>
    </div>
    <div class="col-12">
      <div v-if="showFavoriteList && optionFavoriteList.length > 0" class="row">
        <div class="col-12">
          <section-title
            icon="fa-solid fa-star text-warning"
            content="Favoritos"
            container-class="mt-4 ml-4"
          />
        </div>
        <div
          v-if="optionFavoriteList.length > 0"
          class="col-12 flip-cards-container mt-0 mb-5"
        >
          <square-option
            show-star
            @remove="removeToFavorites(item)"
            v-for="item in optionFavoriteList"
            :key="item.id"
            :option="item"
          />
        </div>
        <div v-else class="d-block ml-auto mr-auto mb-2">
          <empty
            text="Puede oprimir la estrella en las tarjetas para agregarlas como favoritos."
            icon="fal fa-lightbulb-exclamation"
          />
        </div>
        <div id="border"></div>
      </div>
    </div>
    <div class="col-12">
      <div class="row" v-for="option in list" :key="option.id">
        <div class="col-12">
          <section-title
            :icon="option.icon + ' ' + 'font-14'"
            :content="option.name"
            container-class="mt-4 ml-4"
          />
        </div>
        <div class="col-12 flip-cards-container mt-0 mb-5">
          <square-option
            show-star
            @add="addToFavorites(item)"
            @remove="removeToFavorites(item)"
            v-for="item in option.cards"
            :key="item.id"
            :option="item"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import PageHeader from 'PageHeader'
import SquareOption from '@/components/common/menu/SquareOption.vue'
import { name as userModuleName } from '@/modules/user/store'
import { name as commonModuleName } from '@/modules/common/store'
import navMenuTitles from '@/utils/constants/navMenuTitles'
import SectionTitle from 'SectionTitle'
import VSelect from 'vue-select'
import ActionButton from 'ActionButton'
import { addNavToFavorites, removeNavToFavorites } from '@/core/auth'
import { getUserMenu } from '@/modules/user/api'
import { Empty } from '@/components/common'

export default {
  components: {
    SquareOption,
    PageHeader,
    SectionTitle,
    VSelect,
    ActionButton,
    Empty,
  },
  data() {
    return {
      categorySelected: {
        id: 0,
        name: 'Todos',
        icon: 'fas fa-clipboard-list',
      },
      list: [],
      favoriteList: [],
      showFavoriteList: true,
    }
  },
  computed: {
    ...mapState(userModuleName, ['userMenu']),
    ...mapGetters(commonModuleName, ['getFilterText']),
    pageConfig() {
      return {
        icon: 'fas fa-chart-pie',
        title: 'Informes Generales',
        routes: ['Inicio', 'Informes Generales'],
      }
    },
    optionList() {
      const items =
        this.userMenu
          .find((x) => x.title === navMenuTitles.InformesGenerales)
          ?.children.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            detail: item.detail,
            icon: item.iconClasses,
            mainImage: item.mainImagePath,
            hoverImage: item.mainImageHoverPath,
            needSelectedStudent: item.needSelectedStudent,
            route: item.navigationPath,
            isFavorite: item.isFavorite,
            categoryId: item.category?.id,
            categoryName: item.category?.name,
            categoryIcon: item.category?.icon,
          })) || []

      const filteredItems = this.getFilterText
        ? items?.filter((e) =>
            e.title.toLowerCase().match(this.getFilterText.toLowerCase())
          )
        : items

      return this.categoriesOptions
        .map((category) => {
          return {
            ...category,
            cards: [
              ...filteredItems.filter((x) => x.categoryId === category.id).sort((a, b) => this.sortByCardTitle(a,b)),
            ],
          }
        })
        .filter((x) => x.cards.length > 0)
    },
    optionFavoriteList() {
      const items =
        this.userMenu
          .find((x) => x.title === navMenuTitles.InformesGenerales)
          ?.children.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            detail: item.detail,
            icon: item.iconClasses,
            mainImage: item.mainImagePath,
            hoverImage: item.mainImageHoverPath,
            needSelectedStudent: item.needSelectedStudent,
            route: item.navigationPath,
            isFavorite: item.isFavorite,
          })) || []
      return items.filter((x) => x.isFavorite)
    },
    categoriesOptions() {
      const items =
        this.userMenu
          .find((x) => x.title === navMenuTitles.InformesGenerales)
          ?.children.map((item) => ({
            id: item.category?.id,
            name: item.category?.name,
            icon: item.category?.icon,
          }))
          .sort((a, b) => a?.name?.localeCompare(b.name)) || []
      items.unshift(
        {
          id: 0,
          name: 'Todos',
          icon: 'fas fa-clipboard-list',
        },
        {
          id: 0.1,
          name: 'Favoritos',
          icon: 'fa-solid fa-star text-warning',
        }
      )
      return items.filter((a, i) => items.findIndex((s) => a.id === s.id) === i)
    },
    showAllCategories() {
      return this.categorySelected.id === 0
    },
    iconClass() {
      return (icon) =>
        icon.includes('star') ? `${icon} text-warning` : `${icon} text-orange`
    },
    isFavoriteSection() {
      return this.categorySelected.id === 0.1
    },
  },
  watch: {
    getFilterText: {
      immediate: true,
      handler(value) {
        if (value.length === 0) {
          this.showFavoriteList = true
        }
        if (this.showAllCategories && value.length > 0) {
          this.showFavoriteList = false
          this.list = [...this.optionList]
        } else if (!this.showAllCategories && value.length > 0) {
          this.showFavoriteList = false
          this.categorySelected = {
            id: 0,
            name: 'Todos',
            icon: 'fas fa-clipboard-list',
          }
          this.list = [...this.optionList]
        } else if (!this.showAllCategories && value.length === 0) {
          if (this.isFavoriteSection) {
            this.showFavoriteList = true
          }
          this.list = [
            ...this.optionList.filter((x) => x.id === this.categorySelected.id),
          ]
        } else if (this.showAllCategories && value.length === 0) {
          this.list = [...this.optionList]
        }
      },
    },
  },
  mounted() {
    this.list = [...this.optionList]
  },
  methods: {
    ...mapActions(commonModuleName, ['setFilterText']),
    ...mapActions(userModuleName, ['setMenu']),
    setFilterByCategory() {
      if (this.isFavoriteSection || this.showAllCategories) {
        this.showFavoriteList = true
      } else {
        this.showFavoriteList = false
      }
      if (this.showAllCategories) {
        this.list = [...this.optionList]
      } else {
        this.setFilterText('')
        this.list = [
          ...this.optionList.filter((x) => x.id === this.categorySelected.id),
        ]
      }
    },
    sortByCardTitle(a, b) {
      //ascending sort
      const numberPattern = /\d+/
      const titleA = a.title.match(numberPattern);
      const titleB = b.title.match(numberPattern);

      // If title contains numeric sort by numeric value.
      if(titleA && titleB) {
        const numberA = parseInt(titleA[0]);
        const numberB = parseInt(titleB[0]);

        if (numberA !== numberB) {
          return numberA - numberB;
        }
      }

      // If the numeric parts are equal, sort alphabetically
      if (a.title < b.title) {
        return -1;
      }
    },

    async addToFavorites(item) {
      const { data } = await addNavToFavorites(item.id)
      if (data) {
        const userMenu = await getUserMenu()
        if (userMenu && userMenu.length) {
          this.setMenu(userMenu)
          this.list = [...this.optionList]
        }
      }
    },
    async removeToFavorites(item) {
      const { data } = await removeNavToFavorites(item.id)
      if (data) {
        const userMenu = await getUserMenu()
        if (userMenu && userMenu.length) {
          this.setMenu(userMenu)
          this.list = [...this.optionList]
        }
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.setFilterText('')
    next()
  },
}
</script>
<style scoped lang="scss">
span {
  overflow-wrap: break-word;
}

#border {
  width: 96%;
  height: 1px;
  background: yellow;
  text-align: center;
  line-height: 100px;
  background: -webkit-gradient(
      linear,
      left top,
      right top,
      color-stop(50%, orange),
      color-stop(0%, rgba(255, 255, 255, 0))
    ),
    -webkit-gradient(linear, left top, left bottom, color-stop(50%, blue), color-stop(0%, rgba(255, 255, 255, 0))),
    -webkit-gradient(linear, left top, right top, color-stop(50%, green), color-stop(0%, rgba(255, 255, 255, 0))),
    -webkit-gradient(linear, left top, left bottom, color-stop(50%, red), color-stop(0%, rgba(255, 255, 255, 0)));
  background: linear-gradient(to right, #4caf50 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(#4caf50 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(to right, green 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(#4caf50 50%, rgba(255, 255, 255, 0) 0%);
  background-position: top, right, bottom, left;
  background-repeat: repeat-x, repeat-y;
  background-size: 10px 1px, 1px 10px;
  margin-left: 44px;
}

::v-deep .tag-badge-info {
  background: linear-gradient(to right, white, #e2e2e2);

  & div.dot:has(> i.title-icon.fa-solid.fa-star) {
    background-color: white;
  }
}

.v-select.custom-v-select {
  background-color: white;
}

.v-select.custom-v-select >>> .vs__open-indicator {
  cursor: pointer;
}

.custom-ddl >>> .v-select.drop-up.vs--open .vs__dropdown-toggle {
  border-radius: 0 0 4px 4px;
  border-top-color: transparent;
  border-bottom-color: rgba(60, 60, 60, 0.26);
}

.custom-v-select ::v-deep {
  background: white !important;

  .vs__dropdown-menu {
    width: auto !important;
    min-width: 100% !important;
    padding-right: 1.5rem;
  }
}
</style>
